import { liquidoMaximoPeloCet, parcelaPeloCet } from "./teto-cet.js";
/** Tolerancia de centavo: parcela que empata com a margem cabe. */
const EPS = 1e-6;
/**
 * Parcela pelo CET: Price sobre o liquido, sem IOF somado — ja esta dentro do
 * CET (spec 014). Taxa zero amortiza linear.
 */
function parcelaDe(valor, parcelas, taxaAm) {
    return taxaAm > 0 ? parcelaPeloCet({ valorLiquido: valor, parcelas, cetAm: taxaAm }) : valor / parcelas;
}
/**
 * Inverso da Price: maior principal cuja parcela cabe na margem.
 *
 * E' o que responde "entao quanto eu consigo?" quando o valor pedido nao passa.
 */
export function valorMaximoParaMargem(margem, parcelas, taxaAm) {
    return liquidoMaximoPeloCet(margem, parcelas, taxaAm);
}
/** Chave de agrupamento: bancoId quando existe, senao o nome. */
function chaveDoBanco(t) {
    return t.bancoId != null ? `id:${t.bancoId}` : `nome:${t.bancoNome}`;
}
/**
 * Monta a vitrine.
 *
 * Faixas SOBREPOSTAS deixaram de ser aceitas no cadastro (spec 014, R1b), mas
 * tabela gravada antes pode ainda cruzar outra. Nesse caso, pra cada numero de
 * parcelas vale a MENOR taxa entre as que o cobrem: nao ha razao pra cobrar do
 * servidor a mais cara das duas que o proprio banco publicou.
 *
 * Bancos saem ordenados pela menor taxa viavel; os que nao tem nenhuma opcao
 * dentro da margem vao pro fim (mas continuam na lista, com `valorMaximo`, pra
 * tela poder explicar em vez de sumir com eles).
 */
export function montarOpcoesDeCredito({ tabelas, valor, margem, tetoParcelas, }) {
    if (!(valor > 0))
        return [];
    const porBanco = new Map();
    for (const t of tabelas) {
        const k = chaveDoBanco(t);
        const atual = porBanco.get(k);
        if (atual)
            atual.push(t);
        else
            porBanco.set(k, [t]);
    }
    const cards = [];
    for (const doBanco of porBanco.values()) {
        const primeira = doBanco[0];
        const opcoes = [];
        let prazosForaDaMargem = 0;
        let valorMaximo = 0;
        const limite = Math.min(tetoParcelas, doBanco.reduce((max, t) => Math.max(max, t.prazoMaxMeses), 0));
        for (let n = 1; n <= limite; n++) {
            // Tabelas que cobrem este prazo. `prazoMinMeses` ausente = a partir de 1x.
            let melhor;
            for (const t of doBanco) {
                const min = t.prazoMinMeses != null && t.prazoMinMeses > 0 ? t.prazoMinMeses : 1;
                if (n < min || n > t.prazoMaxMeses)
                    continue;
                if (!melhor || t.taxaAm < melhor.taxaAm)
                    melhor = t;
            }
            if (!melhor)
                continue; // buraco na grade do banco — nada a oferecer aqui
            valorMaximo = Math.max(valorMaximo, valorMaximoParaMargem(margem, n, melhor.taxaAm));
            const parcela = parcelaDe(valor, n, melhor.taxaAm);
            if (parcela > margem + EPS) {
                prazosForaDaMargem++;
                continue;
            }
            opcoes.push({
                parcelas: n,
                taxaAm: melhor.taxaAm,
                tabelaId: melhor.id,
                tabelaNome: melhor.nome?.trim() || `Tabela ${melhor.id}`,
                parcela,
                totalPago: parcela * n,
            });
        }
        const taxasViaveis = opcoes.map((o) => o.taxaAm);
        cards.push({
            bancoId: primeira.bancoId,
            bancoNome: primeira.bancoNome,
            convenioId: primeira.convenioId,
            convenio: primeira.convenio,
            opcoes,
            taxaAmMin: taxasViaveis.length > 0 ? Math.min(...taxasViaveis) : 0,
            parcelasMin: opcoes.length > 0 ? opcoes[0].parcelas : 0,
            parcelasMax: opcoes.length > 0 ? opcoes[opcoes.length - 1].parcelas : 0,
            // Prazo curto => parcela alta. Como `opcoes` esta em ordem de prazo, a
            // primeira e' a maior parcela e a ultima, a menor.
            parcelaMax: opcoes.length > 0 ? opcoes[0].parcela : 0,
            parcelaMin: opcoes.length > 0 ? opcoes[opcoes.length - 1].parcela : 0,
            prazosForaDaMargem,
            valorMaximo,
        });
    }
    return cards.sort((a, b) => {
        const aVazio = a.opcoes.length === 0;
        const bVazio = b.opcoes.length === 0;
        if (aVazio !== bVazio)
            return aVazio ? 1 : -1;
        if (aVazio)
            return b.valorMaximo - a.valorMaximo; // quem chega mais perto primeiro
        if (a.taxaAmMin !== b.taxaAmMin)
            return a.taxaAmMin - b.taxaAmMin;
        // Mesma taxa: quem alonga mais o prazo da mais folga na parcela.
        return b.parcelasMax - a.parcelasMax;
    });
}
/**
 * Taxa que vale pra ESTE numero de parcelas, entre as tabelas informadas.
 *
 * Serve pra tela que ja sabe o banco e deixa o servidor mexer so no prazo — a
 * portabilidade, por exemplo. Sem isto ela usaria a menor taxa do banco em
 * qualquer prazo, e a proposta seria recusada na API por cobrar a faixa curta
 * num prazo longo.
 *
 * Sobreposicao resolve pela menor taxa, mesma regra da vitrine.
 *
 * @returns `null` quando nenhuma tabela cobre esse prazo.
 */
export function taxaParaPrazo(tabelas, parcelas) {
    let melhor = null;
    for (const t of tabelas) {
        const min = t.prazoMinMeses != null && t.prazoMinMeses > 0 ? t.prazoMinMeses : 1;
        if (parcelas < min || parcelas > t.prazoMaxMeses)
            continue;
        if (melhor == null || t.taxaAm < melhor)
            melhor = t.taxaAm;
    }
    return melhor;
}
/**
 * A combinacao (banco, prazo, taxa) que o servidor mandou existe mesmo?
 *
 * A vitrine e' montada no navegador, entao a API nao pode confiar no que volta:
 * sem esta conferencia, um payload montado a mao contrata 90x na taxa de 30x.
 */
export function opcaoValida(cards, escolha) {
    const card = cards.find((c) => c.bancoNome === escolha.bancoNome);
    if (!card)
        return false;
    return card.opcoes.some((o) => o.parcelas === escolha.parcelas && Math.abs(o.taxaAm - escolha.taxaAm) <= TOLERANCIA_TAXA);
}
/**
 * Quanto a taxa recebida pode diferir da publicada sem ser considerada outra.
 *
 * 1e-4 em fracao = 0,01 ponto percentual. O tamanho nao e' arbitrario: a taxa
 * viaja pro /termo como percentual com DUAS casas (`(taxa*100).toFixed(2)`), o
 * que sozinho ja introduz erro de ate 0,005 p.p. Uma tolerancia menor recusaria
 * proposta legitima por arredondamento; uma bem maior deixaria passar a faixa
 * vizinha. Entre 3% e 5% — a diferenca que importa — sobra folga de 200x.
 */
export const TOLERANCIA_TAXA = 1e-4;
/** Primeira parcela coberta, com o default do legado aplicado. */
function faixaMinima(table) {
    const min = Number(table.prazoMinMeses ?? 0);
    return Number.isFinite(min) && min > 0 ? min : 1;
}
/**
 * A tabela mais barata entre as que cobrem este prazo.
 *
 * Sobreposicao resolve pela menor taxa: se o banco publicou uma campanha por
 * cima do produto padrao, ele decidiu ofertar as duas — nao ha razao pra cobrar
 * do servidor a mais cara das duas que o proprio banco publicou.
 *
 * @returns `null` quando nenhuma tabela cobre o prazo.
 */
export function tabelaParaPrazo(tables, parcelas) {
    let best = null;
    for (const table of tables) {
        if (parcelas < faixaMinima(table) || parcelas > Number(table.prazoMaxMeses))
            continue;
        if (!best || Number(table.taxaAm) < Number(best.taxaAm))
            best = table;
    }
    return best;
}
/** A taxa recebida e' a publicada pra este prazo (dentro da tolerancia)? */
export function taxaConfere(publicada, recebida) {
    return Math.abs(publicada - recebida) <= TOLERANCIA_TAXA;
}
/**
 * A faixa da tabela cabe no que o convenio autoriza?
 *
 * @returns campo -> motivo, pronto pro envelope de validacao. Vazio = ok.
 */
export function conferirPrazoDaTabela(maxParcelasConvenio, prazoMin, prazoMax) {
    const errors = {};
    if (prazoMin > prazoMax)
        errors.prazoMinMeses = `O prazo inicial (${prazoMin}x) nao pode ser maior que o final (${prazoMax}x).`;
    if (prazoMax > maxParcelasConvenio)
        errors.prazoMaxMeses = `Prazo maximo do convenio e' de ${maxParcelasConvenio} parcelas (teto definido pela prefeitura). Reduza o prazo desta tabela.`;
    return errors;
}
/**
 * A taxa da tabela cabe no teto do convenio?
 *
 * Ate 20/08/2026 esta checagem nao existia: o save propagava a taxa recem
 * digitada PRA CIMA do proprio teto, entao o limite da prefeitura era decorativo
 * neste caminho. Com a propagacao removida, o teto volta a recusar.
 *
 * @param tetoPercent `admin_convenio_configs.taxa_max_am`, em PERCENTUAL (2.5).
 * @param taxaFracao taxa da tabela, em FRACAO (0.025). Confundir erra por 100x.
 */
export function conferirTaxaDaTabela(tetoPercent, taxaFracao) {
    const teto = tetoPercent == null ? NaN : Number(tetoPercent);
    if (!Number.isFinite(teto) || teto <= 0)
        return {}; // convenio sem teto nao restringe
    if (taxaFracao <= teto / 100 + 1e-9)
        return {};
    return {
        taxaAm: `Juros de ${(taxaFracao * 100).toFixed(2)}% a.m. excede o teto de ${teto.toFixed(2)}% definido pela prefeitura para este convenio.`,
    };
}
//# sourceMappingURL=opcoes-credito.js.map